import { StatusBadge } from "@/components/wheel/StatusBadge";
import { WorkspacePanel } from "@/workspace/primitives";

const STATUS_TIER = {
  observed: "info",
  derived: "info",
  cached: "info",
  partial: "caution",
  unavailable: "blocking",
  not_evaluated: "info",
  ready: "info",
  loading: "info",
  error: "blocking",
  skipped: "info",
  idle: "info",
};

export function WorkspaceLiveRequestAudit({ log, resources, testid = "live-request-audit" }) {
  const writes = (log || []).filter((e) => e.method && e.method !== "GET");
  const externals = (log || []).filter((e) => e.externalProvider);
  return (
    <WorkspacePanel
      title="Request audit"
      subtitle="Existing authenticated cache-first GETs only"
      testid={testid}
    >
      <div className="overflow-x-auto min-w-0">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="text-left uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
              <th className="py-1 pr-3 font-medium">Method</th>
              <th className="py-1 pr-3 font-medium">Route</th>
              <th className="py-1 pr-3 font-medium">Tab</th>
              <th className="py-1 pr-3 font-medium">Status</th>
              <th className="py-1 font-medium">ms</th>
            </tr>
          </thead>
          <tbody>
            {(log || []).map((e, i) => (
              <tr key={`${e.path}-${e.startedAt}-${i}`} className="border-t" style={{ borderColor: "var(--hoip-divider)" }}>
                <td className="py-1 pr-3 font-mono-data">{e.method}</td>
                <td className="py-1 pr-3 font-mono-data">{e.path}</td>
                <td className="py-1 pr-3">{e.tab}</td>
                <td className="py-1 pr-3">{e.canceled ? "canceled" : (e.httpStatus || e.error || "—")}</td>
                <td className="py-1 font-mono-data">{e.durationMs ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 space-y-1 text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>
        <div data-testid="audit-writes">Write calls: {writes.length}</div>
        <div data-testid="audit-external">External-provider requests from this preview: {externals.length}</div>
        <div>All listed routes are existing internal GETs. Fetch time is not market observation time.</div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {Object.entries(resources || {}).map(([k, v]) => (
          <StatusBadge key={k} tier={STATUS_TIER[v.status] || "info"} text={`${k}: ${v.status}`} />
        ))}
      </div>
    </WorkspacePanel>
  );
}

export function WorkspaceLiveProvenance({ rows, testid = "live-provenance-table" }) {
  return (
    <WorkspacePanel
      title="Data state"
      subtitle="Source category and field status. Fetch time is listed separately from as-of."
      testid={testid}
    >
      <div className="overflow-x-auto min-w-0">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="text-left uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
              <th className="py-1 pr-3 font-medium">Field</th>
              <th className="py-1 pr-3 font-medium">Status</th>
              <th className="py-1 pr-3 font-medium">As-of</th>
              <th className="py-1 font-medium">Fetched</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.field} className="border-t" style={{ borderColor: "var(--hoip-divider)" }}>
                <td className="py-1 pr-3 font-mono-data align-top">{r.field}</td>
                <td className="py-1 pr-3 align-top">
                  <StatusBadge tier={STATUS_TIER[r.status] || "info"} text={r.status} />
                </td>
                <td className="py-1 pr-3 font-mono-data align-top">{r.asOf || "timestamp unavailable"}</td>
                <td className="py-1 font-mono-data align-top">{r.fetchedAt || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </WorkspacePanel>
  );
}
